import { useState } from 'react';
import { aboutInfo } from '@/mocks/about';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="mobile_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      setFormMsg('消息已发送，我们会尽快回复您！');
      return;
    }
    setFormState('submitting');
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((v, k) => { body.append(k, v as string); });
    try {
      const res = await fetch('https://readdy.ai/api/form/d9srgellbrt1qcgaj8q0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const text = await res.text();
      let msg = '';
      try { const j = JSON.parse(text); msg = j?.meta?.message || j?.message || ''; } catch { /* ignore parse errors */ }
      if (res.ok && !msg.includes('spam') && msg !== 'spam') {
        setFormState('success');
        setFormMsg('消息已发送，我们会尽快回复您！');
        form.reset();
      } else {
        setFormState('error');
        setFormMsg(msg || '发送失败，请稍后再试。');
      }
    } catch {
      setFormState('error');
      setFormMsg('网络错误，请稍后再试。');
    }
  };

  return (
    <>
      <SchemaOrg
        type="Organization"
        data={{ name: '日韩在线' }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 日韩在线',
          description: '日韩在线 - 正版日韩内容一站聚合平台。了解我们的品牌故事、发展历程、用户口碑与联系方式。',
          url: 'https://rikkan-online.com/about',
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://rikkan-online.com' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: aboutInfo.faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
          })),
        }}
      />

      <main className="min-h-screen pt-16">
        {/* Hero */}
        <section className="relative h-[320px] md:h-[400px] overflow-hidden" aria-label="关于我们横幅">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20environment%20with%20warm%20natural%20lighting%2C%20clean%20workspace%20with%20plants%2C%20creative%20atmosphere%2C%20coral%20and%20golden%20accent%20details%2C%20professional%20corporate%20photography%2C%20soft%20shadows&width=1600&height=600&seq=about-hero&orientation=landscape"
            alt="日韩在线办公室"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4">
                  关于日韩在线
                </h1>
                <p className="text-background-50/70 text-base md:text-lg">
                  正版日韩内容一站聚合平台，连接中日韩文化的桥梁
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          {/* Brand Intro */}
          <section className="py-12 md:py-16" aria-label="品牌介绍">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <span className="inline-block px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full mb-4">
                  品牌故事
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                  {aboutInfo.brand.subtitle}
                </h2>
                {aboutInfo.brand.description.map((para, i) => (
                  <p key={i} className="text-foreground-700 leading-relaxed mb-4 text-[15px]">
                    {para}
                  </p>
                ))}
                <div className="mt-6 p-5 bg-primary-50 rounded-xl border-l-[3px] border-primary-400">
                  <p className="text-sm text-foreground-700 italic">
                    <strong className="text-primary-600 not-italic">我们的使命：</strong>
                    {aboutInfo.brand.mission}
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20collaborative%20team%20working%2C%20warm%20lighting%2C%20minimalist%20design%2C%20indoor%20plants%2C%20coral%20and%20golden%20accents%2C%20professional%20corporate%20atmosphere&width=700&height=500&seq=about-office&orientation=landscape"
                  alt="日韩在线团队"
                  className="w-full rounded-xl"
                />
                <div className="absolute -bottom-4 -right-4 bg-accent-500 text-background-50 px-5 py-3 rounded-xl hidden md:block">
                  <p className="text-2xl font-heading font-bold">3年</p>
                  <p className="text-xs opacity-80">深耕日韩内容</p>
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="py-12 md:py-16 border-t border-background-200/70" aria-label="核心价值观">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-accent-50 text-accent-700 text-xs font-medium rounded-full mb-4">
                核心价值观
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                我们坚持的信念
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutInfo.values.map((v) => (
                <div key={v.title} className="text-center p-6 bg-background-100 rounded-xl">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-50">
                    <i className={`${v.icon} text-primary-600 text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Milestones */}
          <section className="py-12 md:py-16 border-t border-background-200/70" aria-label="发展历程">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-secondary-50 text-secondary-700 text-xs font-medium rounded-full mb-4">
                发展历程
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                一路走来
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="relative pl-8 border-l-2 border-background-200 space-y-8">
                {aboutInfo.milestones.map((m, i) => (
                  <div key={i} className="relative">
                    <div className={`absolute -left-[33px] top-1 w-4 h-4 rounded-full border-2 border-background-50 ${
                      i === aboutInfo.milestones.length - 1 ? 'bg-primary-500 border-primary-500' : 'bg-background-300'
                    }`}></div>
                    <div className="bg-background-100 rounded-xl p-4 md:p-5">
                      <span className="inline-block px-2.5 py-0.5 bg-primary-50 text-primary-700 text-xs font-label font-semibold rounded-full mb-2">
                        {m.year}
                      </span>
                      <p className="text-sm text-foreground-700 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-12 md:py-16 border-t border-background-200/70" aria-label="用户口碑">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-accent-50 text-accent-700 text-xs font-medium rounded-full mb-4">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                听听用户怎么说
              </h2>
              <p className="text-sm text-foreground-600">
                来自真实用户的评价与反馈
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutInfo.testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                    <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                      <span className="text-sm font-bold text-secondary-700">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 md:py-16 border-t border-background-200/70" aria-label="常见问题" id="faq">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full mb-4">
                常见问题
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                你可能想知道
              </h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {aboutInfo.faq.map((item, i) => (
                <details key={i} className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between p-5 font-medium text-foreground-900 text-[15px] list-none">
                    {item.question}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-12 md:py-16 border-t border-background-200/70" aria-label="联系我们" id="contact">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <span className="inline-block px-3 py-1 bg-accent-50 text-accent-700 text-xs font-medium rounded-full mb-4">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                  期待与您交流
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，我们的团队将在第一时间回复您。
                </p>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">邮箱</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电话</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">地址</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-time-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.businessHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form="true"
                  className="bg-background-100 rounded-xl p-5 md:p-7 space-y-4"
                  action="https://readdy.ai/api/form/d9srgellbrt1qcgaj8q0"
                  method="POST"
                >
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">发送消息</h3>
                  <p className="text-sm text-foreground-600 mb-3">填写以下表单，我们会尽快与您联系</p>

                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">消息</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="mobile_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                    aria-hidden="true"
                    className="antispam-field"
                  />

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formState === 'submitting' ? '发送中...' : '发送消息'}
                  </button>

                  {formState === 'success' && (
                    <div className="p-3 bg-accent-50 text-accent-700 rounded-lg text-sm flex items-center gap-2">
                      <i className="ri-check-line"></i> {formMsg}
                    </div>
                  )}
                  {formState === 'error' && (
                    <div className="p-3 bg-primary-50 text-primary-700 rounded-lg text-sm flex items-center gap-2">
                      <i className="ri-error-warning-line"></i> {formMsg}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}